<script setup lang="ts">
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import FilterList from '@/onenetwork/components/filter/FilterList.vue'
import { computed, onMounted, ref, watch } from 'vue'
import _ from 'lodash'
import { useConfirm } from 'primevue'
import {
  ListingListApprovalActionTypes,
  useListingListApprovalStore,
} from '@/modules/realestate/stores/listing.list.approval.store'
import { useListingDetailStore } from '@/modules/realestate/stores/listing.detail.store.ts'
import ListingDetail from '@/modules/realestate/views/ListingDetail.vue'
import { useReactiveQueryParam, useUpdateUrlParam } from '@/composables/useReactiveQueryParam'

const confirm = useConfirm()
const listingListApprovalStore = useListingListApprovalStore()
const listingDetailStore = useListingDetailStore()
const idsSelected = ref<Array<string | number>>([])

const pageTabs = computed(() => {
  return listingListApprovalStore.tabs
})
const displayTabContent = computed(() => {
  return listingListApprovalStore.tabs?.find((tab) => tab.active)?.id || 'list'
})
const TableConfig = computed(() => {
  return listingListApprovalStore.tableConfig
})
const filter = computed(() => {
  return listingListApprovalStore.filter
})
const loading = computed(() => {
  return listingListApprovalStore.action_type == ListingListApprovalActionTypes.FETCHING
})
const page = computed(() => {
  return listingListApprovalStore.page
})
const pageSize = computed(() => {
  return listingListApprovalStore.pageSize
})
const dataSource = computed<any>(() => {
  return listingListApprovalStore.data
})

const activeTab = computed(() => {
  return listingListApprovalStore.tabs.find((tab) => tab.active)
})
const detailId: any = useReactiveQueryParam('detailId')
const { updateUrlParam } = useUpdateUrlParam()

const setDetailTab = async () => {
  if (parseInt(detailId.value || 0)) {
    try {
      const data: any = await listingDetailStore.fetchListingDetail(detailId.value)
      listingListApprovalStore.newTab({
        id: data.id,
        label: `#${data.id} - ${data?.title}`,
        active: true,
      })
    } catch (e: any) {
      console.log(e)
    }
  }
}

const handleChangeTab = (tab: any) => {
  listingListApprovalStore.changeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
}

const handleCloseTab = (tab: any) => {
  listingListApprovalStore.closeTab(tab.id)
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
  listingDetailStore.removeListingDetail(tab.id)
}

const handleCloseAllTab = () => {
  listingListApprovalStore.closeAllTab()
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
  listingDetailStore.removeAllListingDetails()
}

const handleChangePage = (page: number) => {
  listingListApprovalStore.changePage(page)
}

const handleChangePageSize = (size: number) => {
  listingListApprovalStore.changePageSize(size)
}

const handleUpdateFilter = (filter: any) => {
  listingListApprovalStore.setFilter(filter)
}

const handleRowDblClick = (e: MouseEvent, item: any) => {
  // console.log(e, item)
  listingListApprovalStore.newTab({
    id: item.code,
    label: `#${item.code} - ${item.subject?.title}`,
    active: true,
  })
  updateUrlParam('detailId', activeTab.value?.id !== 'list' ? activeTab.value?.id : null)
}

const handleRowClick = (e: MouseEvent, item: any) => {}

const handleChangeSelectedRow = (ids: Array<string | number>) => {
  idsSelected.value = ids
}

const handleSort = (field: string, direction: string) => {
  listingListApprovalStore.toggleSort(field, direction)
}

const requestFetchListingList = _.debounce(async () => {
  try {
    const res = await listingListApprovalStore.fetchList()
  } catch (e) {}
}, 500)

const requestReject = async () => {
  try {
    await listingListApprovalStore.bulkReject(idsSelected.value)
    requestFetchListingList()
  } catch (e: any) {
    console.error('Error rejecting listings:', e)
  }
}

const handleReject = async () => {
  if (idsSelected.value.length === 0) {
    return
  }
  confirm.require({
    message: 'Bạn có muốn từ chối các tin đăng đã chọn không?',
    header: 'Xác nhận từ chối',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      requestReject()
    },
    reject: () => {},
  })
}

const requestDelete = async () => {
  try {
    await listingListApprovalStore.bulkDelete(idsSelected.value)
    requestFetchListingList()
  } catch (e: any) {
    console.error('Error deleting listings:', e)
  }
}

const handleDelete = async () => {
  if (idsSelected.value.length === 0) {
    return
  }
  confirm.require({
    message: 'Bạn có muốn xoá các tin đăng đã chọn không?',
    header: 'Xác nhận xoá',
    rejectProps: {
      label: 'Huỷ',
      severity: 'secondary',
      outlined: true,
    },
    acceptProps: {
      label: 'Đồng ý',
    },
    accept: () => {
      requestDelete()
    },
    reject: () => {},
  })
}

watch(
  () => listingListApprovalStore.uuid,
  (newVal, oldVal) => {
    requestFetchListingList()
  },
)

onMounted(() => {
  listingListApprovalStore.changeTab('list')
  if (listingListApprovalStore.action_type === ListingListApprovalActionTypes.PENDING)
    requestFetchListingList()
  if (detailId) setDetailTab()
})
</script>

<template>
  <dashboard-layout
    currentRoute="property/approval"
    page-title="Danh sách tin đăng BDS chờ phê duyệt"
    :tabs="pageTabs"
    :tabsEvent="{
      close: handleCloseTab,
      select: handleChangeTab,
      closeAll: handleCloseAllTab,
    }"
  >
    <template v-if="displayTabContent == 'list'">
      <filter-list :filter="filter" @update:filter="handleUpdateFilter" />
      <data-table
        :loading="loading"
        :tableConfig="TableConfig"
        :page-size="pageSize"
        :page="page"
        :total-page="dataSource?.totalPage || 0"
        :total-records="dataSource?.totalRecords || 0"
        :data="dataSource?.list || []"
        :row-action-buttons="[
          {
            action: handleRowDblClick,
            css: 'btn btn-cs',
            label: 'Chi tiết',
            icon: 'iconamoon:edit-duotone',
            perm: 'real_estate.approve',
          },
        ]"
        :buttons="[
          {
            label: 'Từ chối',
            icon: 'mdi:close-circle-outline',
            class: 'btn btn-warning bg-green-500 text-white !border-0',
            action: handleReject,
            perm: 'real_estate.delete',
          },
          {
            label: 'Xoá tin',
            icon: 'gridicons:create',
            class: 'btn btn-danger text-white !border-0',
            action: handleDelete,
            perm: 'real_estate.delete',
          },
        ]"
        primaryField="code"
        @page-changed="handleChangePage"
        @pagesize-changed="handleChangePageSize"
        @toggle-visibility="listingListApprovalStore.toggleColumnVisibility"
        @on-selected-row="handleChangeSelectedRow"
        @on-sort="handleSort"
      />
    </template>
    <template v-else>
      <template v-for="tab in pageTabs">
        <template v-if="tab.id == displayTabContent">
          <ListingDetail
            :id="tab.id"
            :key="tab.id"
            approveForm
            :reloadList="requestFetchListingList"
          />
        </template>
      </template>
    </template>
  </dashboard-layout>
</template>

<style scoped></style>
